import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Phone, Lock, LogIn, ChevronRight } from 'lucide-react';
import { normalizePhone } from '../utils/phoneUtils';
import toast from 'react-hot-toast';
import AgreementModal from '../components/AgreementModal';
import AuthLayout from '../components/AuthLayout';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { loginWithPassword, acceptTerms, logout } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState<'/' | '/admin' | null>(null);

  const doNavigate = useCallback((path: '/' | '/admin') => {
    setPendingNavigate(null);
    setShowAgreementModal(false);
    navigate(path);
  }, [navigate]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const raw = (phone || '').trim();
      if (!raw) {
        toast.error(t('auth.toastEnterPhone'));
        return;
      }
      if (!(password || '').trim()) {
        toast.error(t('auth.toastEnterPassword'));
        return;
      }
      const phoneE164 = normalizePhone(raw);
      try {
        setLoading(true);
        setPendingNavigate(null);
        const loggedInUser = await loginWithPassword(phoneE164, password.trim());
        if (!loggedInUser) return;
        const needsTerms = !loggedInUser.termsAcceptedAt;
        if (needsTerms) {
          setShowAgreementModal(true);
          setPendingNavigate(loggedInUser.isAdmin ? '/admin' : '/');
        } else {
          if (loggedInUser.isAdmin) navigate('/admin');
          else navigate('/');
        }
      } catch {
        // toast handled in AuthContext
      } finally {
        setLoading(false);
      }
    },
    [phone, password, loginWithPassword, navigate, t]
  );

  const handleAgreementContinue = useCallback(async () => {
    try {
      await acceptTerms();
      if (pendingNavigate) doNavigate(pendingNavigate);
    } catch {
      // toast in context
    }
  }, [acceptTerms, pendingNavigate, doNavigate]);

  const handleAgreementClose = useCallback(() => {
    setShowAgreementModal(false);
    setPendingNavigate(null);
    logout();
    toast.error(t('auth.toastTermsRequired'));
  }, [logout, t]);

  return (
    <AuthLayout 
      title={t('auth.loginTitle')} 
      subtitle={t('auth.welcomeBack')}
    >
      <form className="space-y-6" onSubmit={handleSubmit} autoComplete="off">
        <div className="space-y-2">
          <label htmlFor="phone" className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
            {t('auth.phone')}
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-500 transition-colors">
              <Phone className="h-5 w-5" />
            </div>
            <input
              id="phone"
              name="phoneOrEmail"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="input-premium pl-12 py-3"
              placeholder={t('auth.phonePlaceholder')}
              disabled={loading}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              {t('auth.password')}
            </label>
            <Link to="/forgot-password" className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline">
              {t('auth.forgotPassword')}
            </Link>
          </div>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary-500 transition-colors">
              <Lock className="h-5 w-5" />
            </div>
            <input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-premium pl-12 py-3"
              placeholder={t('auth.passwordPlaceholder')}
              disabled={loading}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading || !phone.trim() || !password.trim()}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-primary-500/20 active:scale-[0.98] transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
        >
          {loading ? (
            <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <LogIn className="w-5 h-5" />
              {t('auth.submitLogin')}
            </>
          )}
        </button>

        <div className="pt-4 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/register" className="font-bold text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-0.5 group">
              {t('auth.registerLink')}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </p>
        </div>

        <div className="mt-8 pt-6 border-t border-slate-100 dark:border-slate-800">
           <p className="text-center text-xs text-slate-400 leading-relaxed italic">
             {t('auth.loginFooterHint')}
           </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/admin-login" className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors uppercase tracking-[0.2em] font-bold">
            {t('auth.adminAccess', 'دخول المدير')}
          </Link>
        </div>
      </form>

      <AgreementModal
        isOpen={showAgreementModal}
        onClose={handleAgreementClose}
        onContinue={handleAgreementContinue}
      />
    </AuthLayout>
  );
};

export default LoginPage;
